import React, { useState, useEffect, useRef, useCallback } from "react";
import { Col, Container, Row, Card, CardHeader, CardBody, Input, Label, Button, Nav, NavItem, NavLink, TabContent, TabPane, FormGroup } from "@/Components/ui";
;
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import { SketchPicker } from "react-color";
import { Save, Plus, Image as ImageIcon, GripVertical, Trash2 } from "lucide-react";
import MyEditor from "../../../Components/editor/MyEditor";
import "quill/dist/quill.snow.css";
import { SITE_ID } from "../../../assets/meta/meta";
import { API, BACKEND_HOST } from "../../../api";
import KakaoMapModal from "./KakaoMapModal";
import CarouselModal from "./CarouselModal";
import ToggleSwitch from "../../../Components/ui/ToggleSwitch";
import MobilePreview from "./MobilePreview";
import DraggableFacilityList from "./DraggableFacilityList";
import DraggableReviewList from "./DraggableReviewList";
import DraggableDisplayMenuList from "./DraggableDisplayMenuList";
import AmenityModal from "./AmenityModal";
import AmenityList from "./AmenityList";
import FacilityModal from "./FacilityModal";
import DisplayMenuModal from "./DisplayMenuModal";
import ReviewModal from "./ReviewModal";
import SettingAdvance from "./SettingAdvance";
import DraggableBannerList from "./DraggableBannerList";
import toast from "react-hot-toast";
import { cn } from "../../../lib/utils";
import { dialogService } from "@/utils/dialogService";
import type { DisplayMenu } from "@/api/type";
import type { RecommendedReview, RecommendedReviewListItem } from "./recommendedReview.types";
import {
    DISPLAY_HOLIDAY_WEEKDAY_OPTIONS,
    buildDisplayHolidaysInfoPayload,
    normalizeDisplayHolidaySettings,
    type DisplayHolidayDate,
    type DisplayHolidayWeekday,
} from "./displayHolidays";
import { buildOperatingHoursInfoPayload, normalizeOperatingHoursFields } from "./operatingHours";
interface AmenityItem {
    id: number;
    name: string;
    image: string | null;
    description: string;
    created_at: string;
}
// Default section visibility structure with ordering
const DEFAULT_SECTION_VISIBILITY = {
    hero_carousel: { enabled: true, order: 1 },
    business_intro: { enabled: true, order: 2 },
    notice: { enabled: true, order: 3 },
    coupon: { enabled: true, order: 4 },
    group_reservation: { enabled: false, order: 5 },
    display_menu: { enabled: true, order: 6 },
    facilities: { enabled: true, order: 7 },
    reviews: { enabled: false, order: 8 },
    sns_links: { enabled: true, order: 9 },
    carousel_gallery: { enabled: true, order: 10 },
    map: { enabled: true, order: 11 },
};
const DEFAULT_MY_PAGE_MORE_MENU_VISIBILITY = {
    amazingpass: true,
    gift_box: true,
    gift_send: true,
    cancel_history: true,
    voucher: true,
    inquiry: true,
};
// Normalize old boolean format to new {enabled, order} format
const normalizeSectionVisibility = (oldData: any): typeof DEFAULT_SECTION_VISIBILITY => {
    const normalized = { ...DEFAULT_SECTION_VISIBILITY };
    if (!oldData)
        return normalized;
    Object.keys(normalized).forEach((key) => {
        if (key in oldData) {
            const oldValue = oldData[key];
            // Handle old boolean format
            if (typeof oldValue === "boolean") {
                normalized[key as keyof typeof DEFAULT_SECTION_VISIBILITY] = {
                    enabled: oldValue,
                    order: normalized[key as keyof typeof DEFAULT_SECTION_VISIBILITY].order,
                };
            }
            else if (typeof oldValue === "object" && oldValue !== null) {
                // Handle new format - preserve or use defaults
                normalized[key as keyof typeof DEFAULT_SECTION_VISIBILITY] = {
                    enabled: oldValue.enabled ?? normalized[key as keyof typeof DEFAULT_SECTION_VISIBILITY].enabled,
                    order: oldValue.order ?? normalized[key as keyof typeof DEFAULT_SECTION_VISIBILITY].order,
                };
            }
        }
    });
    return normalized;
};
const normalizeOptionalHttpUrl = (value: string) => {
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
};
const getOptionalHttpUrlError = (label: string, value: string) => {
    const normalized = normalizeOptionalHttpUrl(value);
    if (!normalized) {
        return null;
    }
    try {
        const parsed = new URL(normalized);
        if (parsed.protocol === "http:" || parsed.protocol === "https:") {
            return null;
        }
    }
    catch (_error) {
    }
    return `${label}은 http:// 또는 https://로 시작하는 올바른 URL이어야 합니다.`;
};
const SiteSettings = () => {
    // Legacy/Basic State
    const [name, setName] = useState("");
    const [address, setAddress] = useState("");
    const [telephone, setTelephone] = useState("");
    const [slogan, setSlogan] = useState("");
    const [footer, setFooter] = useState("");
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [logo, setLogo] = useState<string[]>([]);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [addressRoad, setAddressRoad] = useState("");
    const [addressDetail, setAddressDetail] = useState("");
    const [kakaoPlaceUrl, setKakaoPlaceUrl] = useState("");
    const [naverPlaceUrl, setNaverPlaceUrl] = useState("");
    const [customHeadTags, setCustomHeadTags] = useState("");
    const [groupReservationBankName, setGroupReservationBankName] = useState("");
    const [groupReservationBankAccountNumber, setGroupReservationBankAccountNumber] = useState("");
    const [groupReservationBankAccountHolder, setGroupReservationBankAccountHolder] = useState("");
    const [groupReservationBankNotice, setGroupReservationBankNotice] = useState("");
    const [groupReservationRuleMessage, setGroupReservationRuleMessage] = useState("");
    // SiteInfo Extended State
    const [weekdayOperatingHours, setWeekdayOperatingHours] = useState("");
    const [weekendHolidayOperatingHours, setWeekendHolidayOperatingHours] = useState("");
    const [displayHolidayWeekly, setDisplayHolidayWeekly] = useState<DisplayHolidayWeekday[]>([]);
    const [displayHolidayDates, setDisplayHolidayDates] = useState<DisplayHolidayDate[]>([]);
    const [displayHolidayDateInput, setDisplayHolidayDateInput] = useState("");
    const [displayHolidayMemoInput, setDisplayHolidayMemoInput] = useState("");
    const [detailInfo, setDetailInfo] = useState("");
    const [recommendedReviewTitle, setRecommendedReviewTitle] = useState("");
    const [servicePolicyTitle, setServicePolicyTitle] = useState("");
    const [servicePolicyContent, setServicePolicyContent] = useState("");
    const [privacyPolicyTitle, setPrivacyPolicyTitle] = useState("");
    const [privacyPolicyContent, setPrivacyPolicyContent] = useState("");
    const [mainTitleColor, setMainTitleColor] = useState("#FFFFFF");
    const [showAddress, setShowAddress] = useState(true);
    const [showColorPicker, setShowColorPicker] = useState(false);
    // Splash Screen State
    const [splashScreenEnabled, setSplashScreenEnabled] = useState(false);
    const [splashScreenTitle, setSplashScreenTitle] = useState("");
    const [splashScreenSubtitle, setSplashScreenSubtitle] = useState("");
    // Keep as string to allow empty input
    const [splashScreenDuration, setSplashScreenDuration] = useState("");
    const [splashScreenImageUrl, setSplashScreenImageUrl] = useState<string | null>(null);
    const [splashScreenImageFile, setSplashScreenImageFile] = useState<File | null>(null);
    const [splashScreenImagePreviewUrl, setSplashScreenImagePreviewUrl] = useState<string | null>(null);
    const [sectionVisibility, setSectionVisibility] = useState(DEFAULT_SECTION_VISIBILITY);
    const [bottomBarVisibility, setBottomBarVisibility] = useState({
        ticket: true,
        reservation: true,
        lodging: true,
        shopping: true,
        order: true
    });
    const [myPageMoreMenuVisibility, setMyPageMoreMenuVisibility] = useState(DEFAULT_MY_PAGE_MORE_MENU_VISIBILITY);
    // Related Data State
    const [siteCarousel, setSiteCarousel] = useState<any[]>([]);
    const [galleryCarousels, setGalleryCarousels] = useState<any[]>([]);
    const [facilities, setFacilities] = useState<any[]>([]);
    const [displayMenus, setDisplayMenus] = useState<DisplayMenu[]>([]);
    const [recommendedReviews, setRecommendedReviews] = useState<RecommendedReviewListItem[]>([]);
    const [snsLinks, setSnsLinks] = useState<any[]>([]);
    const [snsItems, setSnsItems] = useState<any[]>([]);
    const [allAmenities, setAllAmenities] = useState<any[]>([]);
    const [selectedAmenityIds, setSelectedAmenityIds] = useState<number[]>([]);
    // Amenity Management State
    const [amenities, setAmenities] = useState<AmenityItem[]>([]);
    const [isAmenityModalOpen, setIsAmenityModalOpen] = useState(false);
    const [selectedAmenity, setSelectedAmenity] = useState<AmenityItem | null>(null);
    const [isAmenityCopyMode, setIsAmenityCopyMode] = useState(false);
    const snsOptions = [
        { value: "instagram", label: "인스타그램" },
        { value: "blog", label: "네이버 블로그" },
        { value: "kakaotalk", label: "카카오톡 채널" }
    ];
    const getSnsPlaceholder = (snsType: string) => {
        if (snsType === "instagram")
            return "https://instagram.com/...";
        if (snsType === "blog")
            return "https://blog.naver.com/...";
        if (snsType === "kakaotalk")
            return "https://pf.kakao.com/...";
        return "https://...";
    };
    // UI State
    const [activeTab, setActiveTab] = useState("1");
    const [kakaoMapEditModal, setKakaoMapEditModal] = useState(false);
    const [carouselEditModal, setCarouselEditModal] = useState(false);
    const [carouselModalNumber, setCarouselModalNumber] = useState(0);
    const [targetCarousel, setTargetCarousel] = useState(-1);
    const [carouselModalType, setCarouselModalType] = useState<"main" | "gallery">("main");
    const [isFacilityModalOpen, setIsFacilityModalOpen] = useState(false);
    const [selectedFacility, setSelectedFacility] = useState<any>(null);
    const [isCopyMode, setIsCopyMode] = useState(false);
    const [isDisplayMenuModalOpen, setIsDisplayMenuModalOpen] = useState(false);
    const [selectedDisplayMenu, setSelectedDisplayMenu] = useState<DisplayMenu | null>(null);
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [selectedReview, setSelectedReview] = useState<RecommendedReview | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    // Refs
    const colorPickerRef = useRef<HTMLDivElement>(null);
    const fillStates = useCallback((data: any) => {
        setName(data.name || "");
        setAddress(data.address || "");
        setTelephone(data.telephone || "");
        setSlogan(data.slogan || "");
        setFooter(data.footer || "");
        setLogoUrl(data.logo || null);
        setLogo([]);
        setLogoPreview(null);
        // Flattened SiteInfo fields
        const normalizedOperatingHours = normalizeOperatingHoursFields(data);
        setWeekdayOperatingHours(normalizedOperatingHours.weekdayOperatingHours);
        setWeekendHolidayOperatingHours(normalizedOperatingHours.weekendHolidayOperatingHours);
        const normalizedDisplayHolidays = normalizeDisplayHolidaySettings(data.display_holidays);
        setDisplayHolidayWeekly(normalizedDisplayHolidays.weekly);
        setDisplayHolidayDates(normalizedDisplayHolidays.dates);
        setDisplayHolidayDateInput("");
        setDisplayHolidayMemoInput("");
        setDetailInfo(data.detail_info || "");
        setRecommendedReviewTitle(data.recommended_review_title || "");
        setServicePolicyTitle(data.service_policy_title || "");
        setServicePolicyContent(data.service_policy_content || "");
        setPrivacyPolicyTitle(data.privacy_policy_title || "");
        setPrivacyPolicyContent(data.privacy_policy_content || "");
        setMainTitleColor(data.main_title_color || "#FFFFFF");
        setShowAddress(data.show_address ?? true);
        // Splash screen fields
        setSplashScreenEnabled(data.splash_screen_enabled ?? false);
        setSplashScreenTitle(data.splash_screen_title || "");
        setSplashScreenSubtitle(data.splash_screen_subtitle || "");
        setKakaoPlaceUrl(data.kakao_place_url || "");
        setNaverPlaceUrl(data.naver_place_url || "");
        setCustomHeadTags(data.custom_head_tags || "");
        setGroupReservationBankName(data.group_reservation_bank_name || "");
        setGroupReservationBankAccountNumber(data.group_reservation_bank_account_number || "");
        setGroupReservationBankAccountHolder(data.group_reservation_bank_account_holder || "");
        setGroupReservationBankNotice(data.group_reservation_bank_notice || "");
        setGroupReservationRuleMessage(data.group_reservation_rule_message || "");
        setSplashScreenDuration(data.splash_screen_duration === null || data.splash_screen_duration === undefined
            ? ""
            : String(data.splash_screen_duration));
        setSplashScreenImageUrl(data.splash_screen_image ?? null);
        setSplashScreenImageFile(null);
        setSplashScreenImagePreviewUrl(null);
        if (data.section_visibility) {
            setSectionVisibility(normalizeSectionVisibility(data.section_visibility));
        }
        if (data.bottom_bar_menu_visibility) {
            setBottomBarVisibility({
                ticket: data.bottom_bar_menu_visibility.ticket ?? true,
                reservation: data.bottom_bar_menu_visibility.reservation ?? true,
                lodging: data.bottom_bar_menu_visibility.lodging ?? true,
                shopping: data.bottom_bar_menu_visibility.shopping ?? true,
                order: data.bottom_bar_menu_visibility.order ?? true
            });
        }
        else {
            setBottomBarVisibility({
                ticket: true,
                reservation: true,
                lodging: true,
                shopping: true,
                order: true,
            });
        }
        if (data.my_page_more_menu_visibility) {
            setMyPageMoreMenuVisibility({
                amazingpass: data.my_page_more_menu_visibility.amazingpass ?? true,
                gift_box: data.my_page_more_menu_visibility.gift_box ?? true,
                gift_send: data.my_page_more_menu_visibility.gift_send ?? true,
                cancel_history: data.my_page_more_menu_visibility.cancel_history ?? true,
                voucher: data.my_page_more_menu_visibility.voucher ?? true,
                inquiry: data.my_page_more_menu_visibility.inquiry ?? true,
            });
        }
        else {
            setMyPageMoreMenuVisibility(DEFAULT_MY_PAGE_MORE_MENU_VISIBILITY);
        }
        if (data.amenities_info) {
            setSelectedAmenityIds(data.amenities_info.map((a: any) => a.id));
        }
    }, []);
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            // 1. Fetch Site & SiteInfo
            const { data: site } = await API.get(`${BACKEND_HOST}/sites/info/${SITE_ID}/`);
            fillStates(site);
            // 2. Fetch Carousels
            const [heroCarouselsResponse, galleryCarouselsResponse] = await Promise.all([
                API.get(`${BACKEND_HOST}/sites/carousel/?site=${SITE_ID}&type=main`),
                API.get(`${BACKEND_HOST}/sites/carousel/?site=${SITE_ID}&type=gallery`),
            ]);
            setSiteCarousel(heroCarouselsResponse.data || []);
            setGalleryCarousels(galleryCarouselsResponse.data || []);
            // 3. Fetch Facilities
            const facRes = await API.get(`${BACKEND_HOST}/sites/facilities/`);
            setFacilities(facRes.data || []);
            // 4. Fetch Recommended Reviews
            const displayMenuRes = await API.get(`${BACKEND_HOST}/sites/display-menus/`);
            setDisplayMenus(displayMenuRes.data || []);
            // 5. Fetch Recommended Reviews
            const revRes = await API.get(`${BACKEND_HOST}/sites/recommended-reviews/`);
            setRecommendedReviews(revRes.data || []);
            // 6. Fetch Available Options (Amenities, Site Types)
            const optsRes = await API.get(`${BACKEND_HOST}/sites/info/${SITE_ID}/?options=true`);
            setAllAmenities(optsRes.data.site_amenities || []);
            // 7. Fetch All Amenities for Management
            const amenitiesRes = await API.get(`${BACKEND_HOST}/sites/amenity-items/`);
            setAmenities(amenitiesRes.data || []);
            // 8. Fetch SNS Links
            if (site.sns_links && site.sns_links.length > 0) {
                setSnsLinks(site.sns_links);
                setSnsItems(site.sns_links.map((link: any) => ({
                    id: link.id,
                    sns: link.sns,
                    url: link.url
                })));
            }
            else {
                setSnsLinks([]);
                setSnsItems([]);
            }
        }
        catch (e) {
            console.error("Fetch data error:", e);
            // alert("데이터를 불러오는데 실패했습니다.");
        }
        finally {
            setLoading(false);
        }
    }, [fillStates]);
    useEffect(() => {
        fetchData();
    }, [fetchData]);
    // Handle clicks outside color picker
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
                setShowColorPicker(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    useEffect(() => {
        return () => {
            if (splashScreenImagePreviewUrl) {
                URL.revokeObjectURL(splashScreenImagePreviewUrl);
            }
        };
    }, [splashScreenImagePreviewUrl]);
    const handleSplashScreenImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        setSplashScreenImageFile(file);
        if (splashScreenImagePreviewUrl) {
            URL.revokeObjectURL(splashScreenImagePreviewUrl);
            setSplashScreenImagePreviewUrl(null);
        }
        if (file) {
            setSplashScreenImagePreviewUrl(URL.createObjectURL(file));
        }
    };
    const saveSplashScreenSettings = async () => {
        try {
            const durationNum = splashScreenDuration === "" ? 0 : Number(splashScreenDuration);
            await API.put(`${BACKEND_HOST}/sites/info/${SITE_ID}/`, {
                info: {
                    splash_screen_enabled: splashScreenEnabled,
                    splash_screen_duration: Number.isFinite(durationNum) ? durationNum : 0,
                    splash_screen_title: splashScreenTitle,
                    splash_screen_subtitle: splashScreenSubtitle,
                },
            });
            if (splashScreenImageFile) {
                const formData = new FormData();
                formData.append("splash_screen_image", splashScreenImageFile);
                await API.put(`${BACKEND_HOST}/sites/info/${SITE_ID}/splash-image/`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
            }
            await dialogService.handleAlert("스플래시 스크린 설정이 저장되었습니다.");
            fetchData();
        }
        catch (e) {
            console.error("Save splash screen settings error:", e);
            await dialogService.handleAlert("스플래시 스크린 설정 저장 중 오류가 발생했습니다.", { force: "error" });
        }
    };
    const handleImageFiles = (e: any, type: string) => {
        const files = e.target.files;
        if (files.length !== 0) {
            let imgFiles: string[] = [];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                let reader = new FileReader();
                reader.onload = () => {
                    imgFiles[i] = reader.result as string;
                    if (type === "logo") {
                        setLogo([...imgFiles]);
                        setLogoPreview(imgFiles[0]);
                    }
                };
                reader.readAsDataURL(file);
            }
        }
    };
    const toggleDisplayHolidayWeekday = (weekday: DisplayHolidayWeekday) => {
        setDisplayHolidayWeekly((prev) => {
            if (prev.includes(weekday)) {
                return prev.filter((item) => item !== weekday);
            }
            return normalizeDisplayHolidaySettings({ weekly: [...prev, weekday], dates: [] }).weekly;
        });
    };
    const addDisplayHolidayDate = () => {
        if (!displayHolidayDateInput) {
            toast.error("휴일 날짜를 선택해주세요.");
            return;
        }
        if (displayHolidayDates.some((item) => item.date === displayHolidayDateInput)) {
            toast.error("이미 등록된 휴일입니다.");
            return;
        }
        const normalized = normalizeDisplayHolidaySettings({
            weekly: [],
            dates: [
                ...displayHolidayDates,
                { date: displayHolidayDateInput, memo: displayHolidayMemoInput },
            ],
        });
        setDisplayHolidayDates(normalized.dates);
        setDisplayHolidayDateInput("");
        setDisplayHolidayMemoInput("");
    };
    const removeDisplayHolidayDate = (date: string) => {
        setDisplayHolidayDates((prev) => prev.filter((item) => item.date !== date));
    };
    const saveHomeStoreSettings = async () => {
        const kakaoPlaceUrlError = getOptionalHttpUrlError("카카오 플레이스 URL", kakaoPlaceUrl);
        const naverPlaceUrlError = getOptionalHttpUrlError("네이버 플레이스 URL", naverPlaceUrl);
        if (kakaoPlaceUrlError || naverPlaceUrlError) {
            toast.error(kakaoPlaceUrlError || naverPlaceUrlError || "지도 링크 형식을 확인해주세요.");
            return;
        }
        const normalizedKakaoPlaceUrl = normalizeOptionalHttpUrl(kakaoPlaceUrl);
        const normalizedNaverPlaceUrl = normalizeOptionalHttpUrl(naverPlaceUrl);
        setIsSaving(true);
        const savePromise = (async () => {
            const siteData: any = {
                name,
                telephone,
                slogan,
            };
            if (logo.length > 0) {
                siteData.logo = [logo[0]];
            }
            else if (!logoUrl && logo.length === 0) {
                siteData.logo = null;
            }
            if (addressRoad) {
                siteData.address = addressRoad + " " + addressDetail;
                await API.put(`/sites/location/${SITE_ID}/`, { address: addressRoad });
            }
            const infoData = {
                ...buildOperatingHoursInfoPayload({
                    weekdayOperatingHours,
                    weekendHolidayOperatingHours,
                }),
                ...buildDisplayHolidaysInfoPayload({
                    weekly: displayHolidayWeekly,
                    dates: displayHolidayDates,
                }),
                kakao_place_url: normalizedKakaoPlaceUrl,
                naver_place_url: normalizedNaverPlaceUrl,
                custom_head_tags: customHeadTags,
                group_reservation_bank_name: groupReservationBankName,
                group_reservation_bank_account_number: groupReservationBankAccountNumber,
                group_reservation_bank_account_holder: groupReservationBankAccountHolder,
                group_reservation_bank_notice: groupReservationBankNotice,
                group_reservation_rule_message: groupReservationRuleMessage,
            };
            await API.put(`/sites/info/${SITE_ID}/`, { ...siteData, info: infoData });
            await saveSnsLinksInternal();
            fetchData();
        })();
        toast.promise(savePromise, {
            loading: "매장 설정을 저장 중입니다...",
            success: "홈 및 매장 설정이 저장되었습니다.",
            error: "저장 중 오류가 발생했습니다.",
        }).finally(() => setIsSaving(false));
    };
    const saveSnsLinksInternal = async () => {
        const snsPromises = [];
        const existingById = new Map(snsLinks.map((link) => [link.id, link]));
        for (const item of snsItems) {
            const trimmedUrl = item.url?.trim();
            const existing = item.id ? existingById.get(item.id) : null;
            if (!trimmedUrl) {
                if (item.id) {
                    snsPromises.push(API.delete(`/sites/link/${item.id}/`));
                }
                continue;
            }
            if (item.id) {
                if (existing && existing.sns !== item.sns) {
                    snsPromises.push(API.delete(`/sites/link/${item.id}/`));
                    snsPromises.push(API.post(`/sites/link/0/`, { sns: item.sns, url: trimmedUrl, site: SITE_ID }));
                }
                else {
                    snsPromises.push(API.put(`/sites/link/${item.id}/`, { url: trimmedUrl }));
                }
            }
            else {
                snsPromises.push(API.post(`/sites/link/0/`, { sns: item.sns, url: trimmedUrl, site: SITE_ID }));
            }
        }
        await Promise.all(snsPromises);
    };
    const saveSnsLinks = async () => {
        setIsSaving(true);
        const savePromise = (async () => {
            await saveSnsLinksInternal();
            fetchData();
        })();
        toast.promise(savePromise, {
            loading: "SNS 링크를 저장 중입니다...",
            success: "SNS 링크가 저장되었습니다.",
            error: "SNS 링크 저장 중 오류가 발생했습니다.",
        }).finally(() => setIsSaving(false));
    };
    const saveIntroSettings = async () => {
        setIsSaving(true);
        const savePromise = (async () => {
            const siteData: any = {
                footer,
            };
            const infoData = {
                detail_info: detailInfo,
          service_policy_title: servicePolicyTitle,
          service_policy_content: servicePolicyContent,
          privacy_policy_title: privacyPolicyTitle,
          privacy_policy_content: privacyPolicyContent,
                main_title_color: mainTitleColor,
                show_address: showAddress,
                section_visibility: sectionVisibility,
                bottom_bar_menu_visibility: bottomBarVisibility,
                my_page_more_menu_visibility: myPageMoreMenuVisibility,
                amenities_info: selectedAmenityIds
            };
            await API.put(`/sites/info/${SITE_ID}/`, { ...siteData, info: infoData });
            fetchData();
        })();
        toast.promise(savePromise, {
            loading: "인트로 설정을 저장 중입니다...",
            success: "인트로 설정이 저장되었습니다.",
            error: "저장 중 오류가 발생했습니다.",
        }).finally(() => setIsSaving(false));
    };
      const saveRecommendedReviewSettings = async () => {
        setIsSaving(true);
        const savePromise = (async () => {
          await API.put(`/sites/info/${SITE_ID}/`, {
            info: {
              recommended_review_title: recommendedReviewTitle,
            },
          });
          fetchData();
        })();
        toast.promise(savePromise, {
          loading: "리뷰 설정을 저장 중입니다...",
          success: "리뷰 설정이 저장되었습니다.",
          error: "저장 중 오류가 발생했습니다.",
        }).finally(() => setIsSaving(false));
      };
    const toggleTab = (tab: string) => {
        if (activeTab !== tab)
            setActiveTab(tab);
    };
    const handleFacilityReorder = async (newOrder: any[]) => {
        setFacilities(newOrder);
        try {
            await API.put(`/sites/facilities/update_order/`, {
                facilities: newOrder.map((f, i) => ({ id: f.id, display_order: i }))
            });
            toast.success("시설 순서가 변경되었습니다.");
        }
        catch (e) {
            console.error("Facility reorder error:", e);
            toast.error("시설 순서 변경에 실패했습니다.");
        }
    };
    const handleReviewReorder = async (newOrder: RecommendedReviewListItem[]) => {
        setRecommendedReviews(newOrder);
        try {
            await API.put(`/sites/recommended-reviews/update_order/`, {
                reviews: newOrder.map((r, i) => ({ id: r.id, display_order: i }))
            });
            toast.success("리뷰 순서가 변경되었습니다.");
        }
        catch (e) {
            console.error("Review reorder error:", e);
            toast.error("리뷰 순서 변경에 실패했습니다.");
        }
    };
    const handleDisplayMenuReorder = async (newOrder: DisplayMenu[]) => {
        setDisplayMenus(newOrder);
        try {
            await API.put(`/sites/display-menus/update_order/`, {
                display_menus: newOrder.map((menu, i) => ({ id: menu.id, display_order: i }))
            });
            toast.success("메뉴 안내 순서가 변경되었습니다.");
        }
        catch (e) {
            console.error("Display menu reorder error:", e);
            toast.error("메뉴 안내 순서 변경에 실패했습니다.");
        }
    };
    const handleBannerReorder = async (newOrder: any[]) => {
        setSiteCarousel(newOrder);
        try {
            await API.put(`/sites/carousels/update-order/`, {
                carousels: newOrder.map((c, i) => ({ id: c.id, display_level: i }))
            });
            toast.success("히어로 이미지 순서가 변경되었습니다.");
        }
        catch (e) {
            console.error("Banner reorder error:", e);
            toast.error("히어로 이미지 순서 변경에 실패했습니다.");
        }
    };
    const handleGalleryCarouselReorder = async (newOrder: any[]) => {
        setGalleryCarousels(newOrder);
        try {
            await API.put(`/sites/carousels/update-order/`, {
                carousels: newOrder.map((c, i) => ({ id: c.id, display_level: i }))
            });
            toast.success("갤러리 순서가 변경되었습니다.");
        }
        catch (e) {
            console.error("Gallery carousel reorder error:", e);
            toast.error("갤러리 순서 변경에 실패했습니다.");
        }
    };
    // Amenity CRUD Handlers
    const handleOpenCreateAmenity = () => {
        setSelectedAmenity(null);
        setIsAmenityCopyMode(false);
        setIsAmenityModalOpen(true);
    };
    const handleOpenEditAmenity = (amenity: AmenityItem) => {
        setSelectedAmenity(amenity);
        setIsAmenityCopyMode(false);
        setIsAmenityModalOpen(true);
    };
    const handleOpenCopyAmenity = (amenity: AmenityItem) => {
        setSelectedAmenity(amenity);
        setIsAmenityCopyMode(true);
        setIsAmenityModalOpen(true);
    };
    const handleSaveAmenity = async (newAmenity: AmenityItem) => {
        if (selectedAmenity && !isAmenityCopyMode) {
            // Update existing
            setAmenities(prev => prev.map(a => a.id === newAmenity.id ? newAmenity : a));
        }
        else {
            // Create new or copy
            setAmenities(prev => [...prev, newAmenity]);
        }
        // Refresh allAmenities for selection UI
        try {
            const res = await API.get(`${BACKEND_HOST}/sites/info/${SITE_ID}/?options=true`);
            setAllAmenities(res.data.site_amenities || []);
        }
        catch (error) {
            console.error("Failed to refresh amenities:", error);
        }
    };
    const handleDeleteAmenity = async (id: number) => {
        if (!await dialogService.confirm("정말 삭제하시겠습니까? 이 편의시설을 선택한 모든 사이트에서 표시되지 않습니다."))
            return;
        try {
            await API.delete(`${BACKEND_HOST}/sites/amenity-items/${id}/`);
            setAmenities(prev => prev.filter(a => a.id !== id));
            // Also remove from selected amenities if present
            setSelectedAmenityIds(prev => prev.filter(aid => aid !== id));
            // Refresh allAmenities
            const res = await API.get(`${BACKEND_HOST}/sites/info/${SITE_ID}/?options=true`);
            setAllAmenities(res.data.site_amenities || []);
            toast.success("편의시설이 삭제되었습니다.");
        }
        catch (error) {
            console.error("Delete amenity error:", error);
            toast.error("편의시설 삭제 중 오류가 발생했습니다.");
        }
    };
    if (loading) {
        return (<div className="page-content flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>);
    }
    return (<React.Fragment>
      <div className="page-content bg-gray-50 min-h-screen">
        <Container fluid>
          <BreadCrumb title="지점 및 랜딩페이지 설정" pageTitle="지점"/>

          {/* Top Bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h4 className="text-xl font-bold text-gray-900">지점 설정</h4>
              <p className="text-sm text-gray-500">랜딩페이지 노출 정보와 시스템 설정을 관리합니다.</p>
            </div>
          </div>

          <Row>
            {/* Left Column: Form Settings */}
            <Col lg={8}>
              <Card className="border-0 shadow-sm rounded-xl overflow-hidden">
                <CardHeader className="bg-white border-b border-gray-100 p-0">
                  <Nav className="nav-tabs-custom nav-success border-0 px-4">
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "1" ? "border-green-500 text-green-600 active" : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => toggleTab("1")}>
                        홈 및 매장 설정
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "5" ? "border-green-500 text-green-600 active" : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => toggleTab("5")}>
                        스플래시 설정
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "2" ? "border-green-500 text-green-600 active" : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => toggleTab("2")}>
                        시설 관리
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "7" ? "border-green-500 text-green-600 active" : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => toggleTab("7")}>
                        메뉴 안내 관리
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "3" ? "border-green-500 text-green-600 active" : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => toggleTab("3")}>
                        리뷰 관리
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "8" ? "border-green-500 text-green-600 active" : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => toggleTab("8")}>
                        갤러리 관리
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "6" ? "border-green-500 text-green-600 active" : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => toggleTab("6")}>
                        편의시설 관리
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink className={cn("px-4 py-3 cursor-pointer font-medium border-b-2 transition-all", activeTab === "4" ? "border-green-500 text-green-600 active" : "border-transparent text-gray-500 hover:text-gray-700")} onClick={() => toggleTab("4")}>
                        고급 설정
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>
                <CardBody className="p-6">
                  <TabContent activeTab={activeTab}>
                    {/* Tab 1: Basic Info */}
                    <TabPane tabId="1">
                      <div className="space-y-6">
                        <section>
                          <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">매장 기본 정보</h5>
                          <Row className="g-4">
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">업체명</Label>
                                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="브랜드 또는 업체 이름" className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">전화번호</Label>
                                <Input value={telephone} onChange={(e) => setTelephone(e.target.value)} placeholder="010-0000-0000" className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">주소</Label>
                                <div className="flex gap-2">
                                  <Input value={addressRoad || address} readOnly className="bg-gray-50 rounded-lg h-10 flex-grow"/>
                                  <Button color="light" className="rounded-lg border-gray-200 shrink-0" onClick={() => setKakaoMapEditModal(true)}>
                                    주소 검색
                                  </Button>
                                </div>
                                {addressRoad && (<Input value={addressDetail} onChange={(e) => setAddressDetail(e.target.value)} placeholder="상세 주소 입력" className="mt-2 rounded-lg h-10"/>)}
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">카카오 플레이스 URL</Label>
                                <Input value={kakaoPlaceUrl} onChange={(e) => setKakaoPlaceUrl(e.target.value)} placeholder="https://place.map.kakao.com/..." className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">네이버 플레이스 URL</Label>
                                <Input value={naverPlaceUrl} onChange={(e) => setNaverPlaceUrl(e.target.value)} placeholder="https://map.naver.com/..." className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                            <Col md={12}>
                              <p className="text-xs text-gray-500 -mt-2">미입력 시 기존 검색 링크를 사용합니다.</p>
                            </Col>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">슬로건 (짧은 한마디)</Label>
                                <Input type="textarea" placeholder="부제목" className="form-control-light" value={slogan} onChange={(e) => setSlogan(e.target.value)} {...({ rows: 2 } as any)}/>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">주중 운영시간</Label>
                                <Input value={weekdayOperatingHours} onChange={(e) => setWeekdayOperatingHours(e.target.value)} placeholder="예: 월-금 10:00 - 21:00" className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">주말·공휴일 운영시간</Label>
                                <Input value={weekendHolidayOperatingHours} onChange={(e) => setWeekendHolidayOperatingHours(e.target.value)} placeholder="예: 토·일·공휴일 10:00 - 22:00" className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">지점 휴일 안내</Label>
                                <p className="text-xs text-gray-500 mb-3">
                                  공휴일 표시는 고객 안내용이며, 영업/예약 차단 여부는 기존 상품/휴무 설정을 따릅니다.
                                </p>
                                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600 mb-2">정기 요일</p>
                                    <div className="flex flex-wrap gap-2">
                                      {DISPLAY_HOLIDAY_WEEKDAY_OPTIONS.map((option) => {
                                          const selected = displayHolidayWeekly.includes(option.value);
                                          return (
                                            <button
                                              key={option.value}
                                              type="button"
                                              className={cn(
                                                "h-9 min-w-10 rounded-lg border px-3 text-sm font-semibold transition-colors",
                                                selected
                                                  ? "border-gray-900 bg-gray-900 text-white"
                                                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-400",
                                              )}
                                              onClick={() => toggleDisplayHolidayWeekday(option.value)}
                                            >
                                              {option.label}
                                            </button>
                                          );
                                      })}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-2 items-end">
                                    <div>
                                      <Label className="text-xs font-semibold text-gray-600">특정일</Label>
                                      <CommonDatePicker
                                        value={displayHolidayDateInput}
                                        clearable
                                        onChangeValue={setDisplayHolidayDateInput}
                                        className="form-control rounded-lg h-10"
                                        placeholder="날짜 선택"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs font-semibold text-gray-600">메모</Label>
                                      <Input
                                        value={displayHolidayMemoInput}
                                        onChange={(e) => setDisplayHolidayMemoInput(e.target.value)}
                                        placeholder="예: 정기 점검"
                                        className="rounded-lg h-10"
                                      />
                                    </div>
                                    <Button color="light" className="rounded-lg border-gray-200 h-10" onClick={addDisplayHolidayDate}>
                                      <Plus size={16} className="mr-1"/> 추가
                                    </Button>
                                  </div>
                                  {displayHolidayDates.length > 0 ? (
                                    <div className="space-y-2">
                                      {displayHolidayDates.map((item) => (
                                        <div key={item.date} className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 sm:flex-row sm:items-center">
                                          <span className="text-sm font-semibold text-gray-900">{item.date}</span>
                                          <span className="text-sm text-gray-600 flex-1">{item.memo || "지점 휴일"}</span>
                                          <Button
                                            color="light"
                                            size="sm"
                                            className="rounded-lg border-gray-200 self-start sm:self-auto"
                                            onClick={() => removeDisplayHolidayDate(item.date)}
                                          >
                                            <Trash2 size={14}/>
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-400">등록된 특정일 휴일이 없습니다.</p>
                                  )}
                                </div>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">지점 로고</Label>
                                <div className="flex gap-2 space-y-3">
                                  {/* 미리보기 */}
                                  {(logoPreview || logoUrl) && (<div className="relative inline-block">
                                      <div className="w-16 h-16 bg-white rounded-lg overflow-hidden border-2 border-gray-200 flex items-center justify-center">
                                        <img src={logoPreview || logoUrl || ""} className="max-w-full max-h-full object-contain" alt="Logo Preview"/>
                                      </div>
                                    </div>)}
                                  {/* 파일 선택 */}
                                  <div className="flex items-center gap-2">
                                    <Input type="file" className="form-control rounded-lg" onChange={(e) => handleImageFiles(e, "logo")} accept="image/*"/>
                                  </div>
                                  {!(logoPreview || logoUrl) && (<p className="text-xs text-gray-500">로고 이미지를 선택해주세요</p>)}
                                </div>
                              </FormGroup>
                            </Col>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">HEAD 태그</Label>
                                <Input
                                  type="textarea"
                                  value={customHeadTags}
                                  onChange={(e) => setCustomHeadTags(e.target.value)}
                                  placeholder={'<meta name="naver-site-verification" content="..." />'}
                                  className="form-control-light font-monospace"
                                  {...({ rows: 6 } as any)}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </section>

                        <section className="pt-6 border-t border-gray-100">
                          <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">
                            단체예약 계좌 정보
                          </h5>
                          <Row className="g-4">
                            <Col md={4}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">은행명</Label>
                                <Input
                                  value={groupReservationBankName}
                                  onChange={(e) => setGroupReservationBankName(e.target.value)}
                                  placeholder="예: 국민은행"
                                  className="rounded-lg h-10"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">계좌번호</Label>
                                <Input
                                  value={groupReservationBankAccountNumber}
                                  onChange={(e) => setGroupReservationBankAccountNumber(e.target.value)}
                                  placeholder="계좌번호"
                                  className="rounded-lg h-10"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">예금주</Label>
                                <Input
                                  value={groupReservationBankAccountHolder}
                                  onChange={(e) => setGroupReservationBankAccountHolder(e.target.value)}
                                  placeholder="예금주명"
                                  className="rounded-lg h-10"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">입금 안내 문구</Label>
                                <Input
                                  type="textarea"
                                  value={groupReservationBankNotice}
                                  onChange={(e) => setGroupReservationBankNotice(e.target.value)}
                                  placeholder="단체예약 입금 안내에 표시할 내용을 입력해주세요."
                                  className="rounded-lg shadow-sm"
                                  {...({ rows: 3 } as any)}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={12}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">단체예약 규정 안내</Label>
                                <Input
                                  type="textarea"
                                  value={groupReservationRuleMessage}
                                  onChange={(e) => setGroupReservationRuleMessage(e.target.value)}
                                  placeholder="예: 학생 수 20명 이상 단체만 접수 가능합니다."
                                  className="rounded-lg shadow-sm"
                                  {...({ rows: 3 } as any)}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        </section>

                        <div className="flex justify-end pt-2">
                          <Button color="dark" className="btn-label rounded-lg px-6" onClick={saveHomeStoreSettings} disabled={isSaving}>
                            <div className="flex items-center gap-2">
                              <Save size={16}/> {isSaving ? "저장 중..." : "기본 정보 저장하기"}
                            </div>
                          </Button>
                        </div>

                        <section className="pt-6 border-t border-gray-100">
                          <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">SNS 링크 설정</h5>
                          <div className="space-y-3">
                            {snsItems.map((item, index) => (<Row className="g-3" key={`${item.id ?? "new"}-${index}`}>
                                <Col md={4}>
                                  <FormGroup>
                                    <Label className="text-sm font-semibold text-gray-700">SNS</Label>
                                    <Input type="select" value={item.sns} onChange={(e) => setSnsItems((prev) => prev.map((entry, i) => i === index ? { ...entry, sns: e.target.value } : entry))} className="rounded-lg h-10">
                                      {snsOptions.map((option) => (<option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>))}
                                    </Input>
                                  </FormGroup>
                                </Col>
                                <Col md={6}>
                                  <FormGroup>
                                    <Label className="text-sm font-semibold text-gray-700">링크</Label>
                                    <Input value={item.url} onChange={(e) => setSnsItems((prev) => prev.map((entry, i) => i === index ? { ...entry, url: e.target.value } : entry))} placeholder={getSnsPlaceholder(item.sns)} className="rounded-lg h-10"/>
                                  </FormGroup>
                                </Col>
                                <Col md={2} className="flex items-end">
                                  <Button color="light" className="rounded-lg border-gray-200 h-10 w-full" onClick={() => setSnsItems((prev) => prev.filter((_, i) => i !== index))}>
                                    삭제
                                  </Button>
                                </Col>
                              </Row>))}
                          </div>
                          <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
                            <Button color="light" className="rounded-lg border-gray-200" onClick={() => setSnsItems((prev) => [
            ...prev,
            { id: null, sns: "instagram", url: "" }
        ])}>
                              SNS 링크 추가
                            </Button>
                            <Button color="dark" className="btn-label rounded-lg px-6" onClick={saveSnsLinks} disabled={isSaving}>
                              <div className="flex items-center gap-2">
                                <Save size={16}/> {isSaving ? "저장 중..." : "SNS 저장하기"}
                              </div>
                            </Button>
                          </div>
                        </section>

                        {/* ===== 상세 정보 설정 (기존 인트로 설정) ===== */}
                        <section className="pt-8 border-t-4 border-gray-200">
                          <h4 className="text-lg font-bold text-gray-900 mb-6 border-l-4 border-green-500 pl-3">상세 정보 설정</h4>

                          <div className="space-y-8">
                            <section>
                              <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">히어로 및 스타일</h5>
                              <Row className="g-4">
                                <Col md={6}>
                                  <FormGroup>
                                    <Label className="text-sm font-semibold text-gray-700">메인 타이틀 색상</Label>
                                    <div className="flex items-center gap-3 relative">
                                      <div className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer shadow-sm transition-transform hover:scale-105" style={{ backgroundColor: mainTitleColor }} onClick={() => setShowColorPicker(!showColorPicker)}/>
                                      <Input value={mainTitleColor} onChange={(e) => setMainTitleColor(e.target.value)} className="w-32 rounded-lg h-10 font-mono text-xs"/>
                                      {showColorPicker && (<div className="absolute z-50 top-12 left-0 shadow-2xl" ref={colorPickerRef}>
                                          <SketchPicker color={mainTitleColor} onChangeComplete={(color) => setMainTitleColor(color.hex)}/>
                                        </div>)}
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-2">밝은 이미지 배경에는 어두운 색상(#000000)을 권장합니다.</p>
                                  </FormGroup>
                                </Col>
                                <Col md={6}>
                                  <FormGroup>
                                    <Label className="text-sm font-semibold text-gray-700">주소 노출 여부</Label>
                                    <ToggleSwitch checked={showAddress} onChange={setShowAddress} label={showAddress ? "랜딩 페이지 상단에 주소 표시함" : "주소 숨김 (이름만 표시)"} className="mt-2"/>
                                  </FormGroup>
                                </Col>
                              </Row>
                            </section>

                            <section className="pt-6 border-t border-gray-100">
                              <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">인트로 정보 관리</h5>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">지점 상세 소개 (인트로 텍스트)</Label>
                                <Input type="textarea" value={detailInfo} onChange={(e) => setDetailInfo(e.target.value)} {...({ rows: 5 } as any)} placeholder="랜딩페이지 업체 소개 섹션에 표시될 상세 텍스트를 입력해주세요." className="rounded-lg shadow-sm"/>
                                <p className="text-[10px] text-gray-400 mt-2">* 줄바꿈이 그대로 웹페이지에 반영됩니다.</p>
                              </FormGroup>
                            </section>

                            <section className="pt-6 border-t border-gray-100">
                              <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">리뷰 및 법적 문서 문구</h5>
                              <Row className="g-4">
                                <Col md={6}>
                                  <FormGroup>
                                    <Label className="text-sm font-semibold text-gray-700">서비스 이용약관 제목</Label>
                                    <Input value={servicePolicyTitle} onChange={(e) => setServicePolicyTitle(e.target.value)} placeholder="예: 서비스 이용약관" className="rounded-lg h-10"/>
                                  </FormGroup>
                                </Col>
                                <Col md={6}>
                                  <FormGroup>
                                    <Label className="text-sm font-semibold text-gray-700">개인정보처리방침 제목</Label>
                                    <Input value={privacyPolicyTitle} onChange={(e) => setPrivacyPolicyTitle(e.target.value)} placeholder="예: 개인정보처리방침" className="rounded-lg h-10"/>
                                  </FormGroup>
                                </Col>
                                <Col md={12}>
                                  <FormGroup>
                                    <Label className="text-sm font-semibold text-gray-700">서비스 이용약관 내용</Label>
                                    <MyEditor
                                      id="service-policy-editor"
                                      uid="service-policy-editor"
                                      value={servicePolicyContent}
                                      onChange={(val: string) => setServicePolicyContent(val)}
                                      noImage={true}
                                    />
                                    <p className="text-[10px] text-gray-400 mt-2">* 푸터와 동일한 서식 편집기가 적용되며, 저장 후 웹 약관 페이지에 같은 형식으로 노출됩니다.</p>
                                  </FormGroup>
                                </Col>
                                <Col md={12}>
                                  <FormGroup>
                                    <Label className="text-sm font-semibold text-gray-700">개인정보처리방침 내용</Label>
                                    <MyEditor
                                      id="privacy-policy-editor"
                                      uid="privacy-policy-editor"
                                      value={privacyPolicyContent}
                                      onChange={(val: string) => setPrivacyPolicyContent(val)}
                                      noImage={true}
                                    />
                                    <p className="text-[10px] text-gray-400 mt-2">* 에디터 서식은 고객 웹의 개인정보처리방침 페이지에 그대로 반영됩니다.</p>
                                  </FormGroup>
                                </Col>
                              </Row>
                            </section>

                            <section className="pt-6 border-t border-gray-100">
                              <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">편의시설 및 서비스 관리</h5>
                              <div className="bg-gray-50 rounded-xl p-4">
                                <p className="text-xs text-gray-500 mb-4">* 랜딩페이지에 노출할 편의시설을 선택해주세요. 선택된 항목만 아이콘과 함께 표시됩니다.</p>
                                <div className="flex flex-wrap gap-2">
                                  {allAmenities.map((amenity) => (<div key={amenity.id} onClick={() => {
                if (selectedAmenityIds.includes(amenity.id)) {
                    setSelectedAmenityIds(p => p.filter(id => id !== amenity.id));
                }
                else {
                    setSelectedAmenityIds(p => [...p, amenity.id]);
                }
            }} className={cn("inline-flex items-center gap-2 px-3 py-2 rounded-full border transition-all cursor-pointer font-medium text-sm", selectedAmenityIds.includes(amenity.id)
                ? "bg-green-500 text-white border-green-500 shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:border-gray-300")}>
                                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                        {amenity.image ? (<img src={amenity.image} alt={amenity.name} className="w-full h-full object-contain"/>) : (<ImageIcon size={14}/>)}
                                      </div>
                                      <span>
                                        {amenity.name}
                                      </span>
                                    </div>))}
                                </div>
                                {allAmenities.length === 0 && (<div className="py-8 text-center text-gray-400 text-xs italic">
                                    설정 가능한 편의시설 정보가 없습니다.
                                  </div>)}
                              </div>
                              <div className="flex items-center justify-between">
                                <Button color="soft-primary" size="sm" onClick={() => toggleTab("6")}>
                                  <Plus size={14} className="mr-1"/> 편의시설 추가 / 관리
                                </Button>
                              </div>
                            </section>

                            <section className="pt-6 border-t border-gray-100">
                              <div className="flex items-center justify-between mb-4">
                                <h5 className="text-md font-bold text-gray-900 border-l-4 border-black pl-3 uppercase tracking-tight">히어로 캐러셀 관리</h5>
                                {siteCarousel.length === 0 && (<Button color="soft-primary" size="sm" onClick={() => { setCarouselModalType("main"); setTargetCarousel(-1); setCarouselEditModal(true); setCarouselModalNumber(-1); }}>
                                  <Plus size={14} className="mr-1"/> 히어로 그룹 생성
                                </Button>)}
                              </div>
                              {siteCarousel.length > 0 ? (<p className="text-xs text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
                                히어로 캐러셀 그룹은 1개만 유지됩니다. 현재 그룹 안에서 이미지를 여러 장 추가, 삭제, 정렬한 뒤 저장하세요.
                              </p>) : (<p className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4">
                                히어로 캐러셀 그룹이 없습니다. 그룹을 생성한 뒤 이미지를 여러 장 등록할 수 있습니다.
                              </p>)}
                              <DraggableBannerList carousels={siteCarousel} onReorder={handleBannerReorder} onEdit={(index, id) => {
            setCarouselModalType("main");
            setCarouselEditModal(true);
            setCarouselModalNumber(index);
            setTargetCarousel(id);
        }}/>
                            </section>

                            <section className="pt-6 border-t border-gray-100">
                              <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">섹션 노출 및 순서 관리</h5>
                              <p className="text-xs text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
                                ℹ️ 드래그하여 섹션의 노출 순서를 변경할 수 있습니다. 토글로 섹션 노출 여부를 제어합니다.
                              </p>
                              <div className="space-y-2 bg-gray-50 rounded-xl p-4 border border-gray-100">
                                {Object.entries(sectionVisibility)
            .sort(([, a], [, b]) => (a.order ?? 99) - (b.order ?? 99))
            .map(([key, section], index) => (<div key={key} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow group" draggable onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("sectionKey", key);
            }} onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add("border-blue-400", "bg-blue-50");
            }} onDragLeave={(e) => {
                e.currentTarget.classList.remove("border-blue-400", "bg-blue-50");
            }} onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("border-blue-400", "bg-blue-50");
                const sourceKey = e.dataTransfer.getData("sectionKey");
                if (sourceKey !== key) {
                    const sourceOrder = sectionVisibility[sourceKey as keyof typeof sectionVisibility]?.order ?? 99;
                    const targetOrder = section.order ?? 99;
                    setSectionVisibility({
                        ...sectionVisibility,
                        [sourceKey]: { ...sectionVisibility[sourceKey as keyof typeof sectionVisibility], order: targetOrder },
                        [key]: { ...section, order: sourceOrder },
                    });
                }
            }}>
                                      <GripVertical size={18} className="text-gray-400 group-hover:text-gray-600 cursor-grab active:cursor-grabbing"/>
                                      <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded min-w-max">{index + 1}</span>
                                      <span className="text-sm font-medium text-gray-700 min-w-max">
                                        {key === 'hero_carousel' && '히어로 캐러셀'}
                                        {key === 'business_intro' && '업체 소개'}
                                        {key === 'notice' && '공지사항'}
                                        {key === 'coupon' && '쿠폰 / 이벤트'}
                                        {key === 'group_reservation' && '단체 예약 신청'}
                                        {key === 'display_menu' && '메뉴 안내'}
                                        {key === 'reviews' && '추천 리뷰'}
                                        {key === 'facilities' && '시설 안내'}
                                        {key === 'sns_links' && 'SNS 링크'}
                                        {key === 'carousel_gallery' && '갤러리 캐러셀'}
                                        {key === 'map' && '위치 지도'}
                                      </span>
                                      <div className="ml-auto">
                                        <ToggleSwitch checked={section.enabled} onChange={(v) => setSectionVisibility({
                ...sectionVisibility,
                [key]: { ...section, enabled: v },
            })} label=""/>
                                      </div>
                                    </div>))}
                              </div>
                            </section>

                            <section className="pt-6 border-t border-gray-100">
                              <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">하단 바 메뉴 노출 관리</h5>
                              <div className="bg-white border border-gray-200 rounded-xl p-4">
                                <div className="flex flex-wrap gap-6 items-center">
                                  <ToggleSwitch checked={true} onChange={() => { }} label="홈 (기본)" disabled/>
                                  <ToggleSwitch checked={bottomBarVisibility.ticket} onChange={(v) => setBottomBarVisibility(p => ({ ...p, ticket: v }))} label="티켓"/>
                                  <ToggleSwitch checked={bottomBarVisibility.reservation} onChange={(v) => setBottomBarVisibility(p => ({ ...p, reservation: v }))} label="예약"/>
                                  <ToggleSwitch checked={bottomBarVisibility.lodging} onChange={(v) => setBottomBarVisibility(p => ({ ...p, lodging: v }))} label="숙박"/>
                                  <ToggleSwitch checked={bottomBarVisibility.shopping} onChange={(v) => setBottomBarVisibility(p => ({ ...p, shopping: v }))} label="쇼핑"/>
                                  <ToggleSwitch checked={bottomBarVisibility.order} onChange={(v) => setBottomBarVisibility(p => ({ ...p, order: v }))} label="오더"/>
                                  <ToggleSwitch checked={true} onChange={() => { }} label="MY (기본)" disabled/>
                                </div>
                              </div>
                            </section>

                            <section className="pt-6 border-t border-gray-100">
                              <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">마이페이지 더보기 노출 관리</h5>
                              <div className="bg-white border border-gray-200 rounded-xl p-4">
                                <div className="flex flex-wrap gap-6 items-center">
                                  <ToggleSwitch checked={myPageMoreMenuVisibility.amazingpass} onChange={(v) => setMyPageMoreMenuVisibility((p) => ({ ...p, amazingpass: v }))} label="어메이징패스"/>
                                  <ToggleSwitch checked={myPageMoreMenuVisibility.gift_box} onChange={(v) => setMyPageMoreMenuVisibility((p) => ({ ...p, gift_box: v }))} label="선물함"/>
                                  <ToggleSwitch checked={myPageMoreMenuVisibility.gift_send} onChange={(v) => setMyPageMoreMenuVisibility((p) => ({ ...p, gift_send: v }))} label="보낸선물"/>
                                  <ToggleSwitch checked={myPageMoreMenuVisibility.cancel_history} onChange={(v) => setMyPageMoreMenuVisibility((p) => ({ ...p, cancel_history: v }))} label="취소내역"/>
                                  <ToggleSwitch checked={myPageMoreMenuVisibility.inquiry ?? true} onChange={(v) => setMyPageMoreMenuVisibility((p) => ({ ...p, inquiry: v }))} label="문의하기"/>
                                </div>
                              </div>
                            </section>

                            <section className="pt-6 border-t border-gray-100">
                              <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">푸터(Footer) 에디터</h5>
                              <MyEditor id="site-footer" uid="site-footer" value={footer} onChange={(val: string) => setFooter(val)} noImage={true}/>
                            </section>

                            <div className="flex justify-end pt-4 border-t border-gray-100">
                              <Button color="dark" className="btn-label rounded-lg px-6" onClick={saveIntroSettings} disabled={isSaving}>
                                <div className="flex items-center gap-2">
                                  <Save size={16}/> {isSaving ? "저장 중..." : "저장하기"}
                                </div>
                              </Button>
                            </div>
                          </div>
                        </section>
                      </div>
                    </TabPane>

                    {/* Tab 2: Facility Management */}
                    <TabPane tabId="2">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h5 className="text-md font-bold text-gray-900 border-l-4 border-black pl-3 uppercase tracking-tight">시설 관리</h5>
                          <Button color="dark" size="sm" className="rounded-lg px-4" onClick={() => { setSelectedFacility(null); setIsFacilityModalOpen(true); setIsCopyMode(false); }} disabled={isSaving}>
                            <Plus size={16} className="mr-1"/> 시설 추가
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
                          * 드래그하여 노출 순서를 변경할 수 있습니다. 이미지는 실제 랜딩페이지 시설 섹션에서 격자 형태로 표시됩니다.
                        </p>
                        <DraggableFacilityList facilities={facilities} onReorder={handleFacilityReorder} onEdit={(f) => { setSelectedFacility(f); setIsFacilityModalOpen(true); setIsCopyMode(false); }} onCopy={(f) => { setSelectedFacility(f); setIsFacilityModalOpen(true); setIsCopyMode(true); }} onDelete={async (id) => {
            if (await dialogService.confirm("정말 이 시설을 삭제하시겠습니까?")) {
                try {
                    await API.delete(`/sites/facilities/${id}/`);
                    setFacilities(p => p.filter(f => f.id !== id));
                    toast.success("시설이 삭제되었습니다.");
                }
                catch (e) {
                    console.error("Facility deletion error:", e);
                    toast.error("시설 삭제 중 오류가 발생했습니다.");
                }
            }
        }} onToggleActive={async (id) => {
            try {
                const fac = facilities.find(f => f.id === id);
                const res = await API.patch(`/sites/facilities/${id}/`, { is_active: !fac.is_active });
                setFacilities(p => p.map(f => f.id === id ? res.data : f));
                toast.success(res.data.is_active ? "시설이 활성화되었습니다." : "시설이 비활성화되었습니다.");
            }
            catch (e) {
                console.error("Facility toggle error:", e);
                toast.error("상태 변경 중 오류가 발생했습니다.");
            }
        }}/>
                      </div>
                    </TabPane>

                    {/* Tab 3: Review Management */}
                    <TabPane tabId="7">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h5 className="text-md font-bold text-gray-900 border-l-4 border-black pl-3 uppercase tracking-tight">메뉴 안내 관리</h5>
                          <Button color="dark" size="sm" className="rounded-lg px-4" onClick={() => { setSelectedDisplayMenu(null); setIsDisplayMenuModalOpen(true); }}>
                            <Plus size={16} className="mr-1"/> 메뉴 추가
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 bg-amber-50 p-3 rounded-lg border border-amber-100 mb-4">
                          * 랜딩페이지 전용 메뉴 소개 카드입니다. 판매 메뉴나 테이블오더 메뉴와는 별개로 운영됩니다.<br />
                          * 드래그하여 노출 순서를 변경할 수 있으며, "더보기" 페이지에서도 같은 순서로 보여집니다.
                        </p>
                        <DraggableDisplayMenuList displayMenus={displayMenus} onReorder={handleDisplayMenuReorder} onEdit={(displayMenu) => {
            setSelectedDisplayMenu(displayMenu);
            setIsDisplayMenuModalOpen(true);
        }} onDelete={async (id) => {
            if (await dialogService.confirm("정말 이 메뉴 안내를 삭제하시겠습니까?")) {
                try {
                    await API.delete(`/sites/display-menus/${id}/`);
                    setDisplayMenus((prev) => prev.filter((item) => item.id !== id));
                    toast.success("메뉴 안내가 삭제되었습니다.");
                }
                catch (e) {
                    console.error("Display menu deletion error:", e);
                    toast.error("메뉴 안내 삭제 중 오류가 발생했습니다.");
                }
            }
        }} onToggleActive={async (id) => {
            try {
                const current = displayMenus.find((item) => item.id === id);
                if (!current) {
                    return;
                }
                const data = new FormData();
                data.append("title", current.title);
                data.append("description", current.description || "");
                data.append("is_active", String(!current.is_active));
                const res = await API.patch(`/sites/display-menus/${id}/`, data);
                setDisplayMenus((prev) => prev.map((item) => item.id === id ? res.data : item));
                toast.success(res.data.is_active ? "메뉴 안내가 활성화되었습니다." : "메뉴 안내가 비활성화되었습니다.");
            }
            catch (e) {
                console.error("Display menu toggle error:", e);
                toast.error("상태 변경 중 오류가 발생했습니다.");
            }
        }}/>
                      </div>
                    </TabPane>

                    {/* Tab 3: Review Management */}
                    <TabPane tabId="3">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h5 className="text-md font-bold text-gray-900 border-l-4 border-black pl-3 uppercase tracking-tight">추천 리뷰 관리</h5>
                          <Button color="dark" size="sm" className="rounded-lg px-4" onClick={() => { setSelectedReview(null); setIsReviewModalOpen(true); }}>
                            <Plus size={16} className="mr-1"/> 리뷰 추가
                          </Button>
                        </div>
                        <section className="rounded-xl border border-gray-200 bg-white p-4">
                          <Row className="g-3 items-end">
                            <Col md={8}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">추천 리뷰 섹션 제목</Label>
                                <Input value={recommendedReviewTitle} onChange={(e) => setRecommendedReviewTitle(e.target.value)} placeholder="예: 우수 리뷰 추천" className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <Button color="dark" className="btn-label rounded-lg px-6 w-full md:w-auto" onClick={saveRecommendedReviewSettings} disabled={isSaving}>
                                <div className="flex items-center justify-center gap-2">
                                  <Save size={16}/> {isSaving ? "저장 중..." : "제목 저장하기"}
                                </div>
                              </Button>
                            </Col>
                          </Row>
                        </section>
                        <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4 line-relaxed">
                          * 인스타그램, 네이버 블로그 등 외부 플랫폼의 우수 리뷰를 등록하여 랜딩페이지에 노출합니다.<br />
                          * 드래그하여 노출 순서를 조절할 수 있습니다.
                        </p>
                        <DraggableReviewList reviews={recommendedReviews} onReorder={handleReviewReorder} onEdit={(r) => { setSelectedReview(r); setIsReviewModalOpen(true); }} onDelete={async (id) => {
            if (await dialogService.confirm("정말 이 리뷰를 삭제하시겠습니까?")) {
                try {
                    await API.delete(`/sites/recommended-reviews/${id}/`);
                    setRecommendedReviews(p => p.filter(r => r.id !== id));
                    toast.success("리뷰가 삭제되었습니다.");
                }
                catch (e) {
                    console.error("Review deletion error:", e);
                    toast.error("리뷰 삭제 중 오류가 발생했습니다.");
                }
            }
        }} onToggleActive={async (id) => {
            try {
                const rev = recommendedReviews.find(r => r.id === id);
                if (!rev) {
                    return;
                }
                const res = await API.put(`/sites/recommended-reviews/${id}/`, { ...rev, is_active: !rev.is_active });
                setRecommendedReviews(p => p.map(r => r.id === id ? res.data : r));
                toast.success(res.data.is_active ? "리뷰가 활성화되었습니다." : "리뷰가 비활성화되었습니다.");
            }
            catch (e) {
                console.error("Review toggle error:", e);
                toast.error("상태 변경 중 오류가 발생했습니다.");
            }
        }}/>
                      </div>
                    </TabPane>

                    {/* Tab 8: Gallery Management */}
                    <TabPane tabId="8">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h5 className="text-md font-bold text-gray-900 border-l-4 border-black pl-3 uppercase tracking-tight">갤러리 관리</h5>
                          <Button color="dark" size="sm" className="rounded-lg px-4" onClick={() => {
            setCarouselModalType("gallery");
            setTargetCarousel(-1);
            setCarouselEditModal(true);
            setCarouselModalNumber(-1);
        }}>
                            <Plus size={16} className="mr-1"/> 갤러리 그룹 추가
                          </Button>
                        </div>
                        <p className="text-xs text-blue-600 bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
                          갤러리 그룹은 여러 개 등록할 수 있습니다. 각 그룹 안에서 이미지를 여러 장 추가, 삭제, 정렬한 뒤 저장하세요.
                        </p>
                        <DraggableBannerList carousels={galleryCarousels} onReorder={handleGalleryCarouselReorder} onEdit={(index, id) => {
            setCarouselModalType("gallery");
            setCarouselEditModal(true);
            setCarouselModalNumber(index);
            setTargetCarousel(id);
        }} emptyText="등록된 갤러리 그룹이 없습니다." groupLabel="갤러리 그룹" imageCountLabel="이미지" showPrimaryBadge={false}/>
                      </div>
                    </TabPane>

                    {/* Tab 4: Advanced Settings */}
                    <TabPane tabId="4">
                      <SettingAdvance />
                    </TabPane>

                    {/* Tab 6: Splash Screen Settings */}
                    <TabPane tabId="5">
                      <div className="space-y-6">
                        <section>
                          <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">스플래시 스크린 설정</h5>
                          <Row className="g-4">
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">노출 여부</Label>
                                <ToggleSwitch checked={splashScreenEnabled} onChange={setSplashScreenEnabled} label={splashScreenEnabled ? "스플래시 스크린을 노출합니다" : "스플래시 스크린을 숨깁니다"} className="mt-2"/>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">자동 닫힘 시간 (ms)</Label>
                                <Input type="number" value={splashScreenDuration} onChange={(e) => setSplashScreenDuration(e.target.value)} placeholder="2500" className="rounded-lg h-10"/>
                                <p className="text-[10px] text-gray-400 mt-2">비우거나 0이면 기본 2500ms</p>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">타이틀</Label>
                                <Input value={splashScreenTitle} onChange={(e) => setSplashScreenTitle(e.target.value)} placeholder="예: 환영합니다" className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">서브 타이틀</Label>
                                <Input value={splashScreenSubtitle} onChange={(e) => setSplashScreenSubtitle(e.target.value)} placeholder="예: 잠시만 기다려주세요" className="rounded-lg h-10"/>
                              </FormGroup>
                            </Col>
                          </Row>
                        </section>

                        <section className="pt-6 border-t border-gray-100">
                          <h5 className="text-md font-bold text-gray-900 mb-4 border-l-4 border-black pl-3 uppercase tracking-tight">이미지</h5>
                          <Row className="g-4">
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">스플래시 이미지 업로드</Label>
                                <Input type="file" className="form-control rounded-lg" onChange={handleSplashScreenImageFile} accept="image/*"/>
                                <p className="text-[10px] text-gray-400 mt-2">* 저장 버튼을 누르면 업로드됩니다.</p>
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="text-sm font-semibold text-gray-700">미리보기</Label>
                                <div className="w-full h-[140px] bg-gray-50 rounded-xl overflow-hidden border border-gray-200 flex items-center justify-center">
                                  {splashScreenImagePreviewUrl || splashScreenImageUrl ? (<img src={(splashScreenImagePreviewUrl || splashScreenImageUrl) as string} alt="Splash preview" className="w-full h-full object-cover"/>) : (<div className="flex flex-col items-center justify-center text-gray-400">
                                      <ImageIcon size={18}/>
                                      <span className="text-[10px] mt-2">NO IMAGE</span>
                                    </div>)}
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>
                        </section>

                        <div className="flex justify-end pt-4 border-t border-gray-100">
                          <Button color="dark" className="btn-label rounded-lg px-6" onClick={saveSplashScreenSettings}>
                            <div className="flex items-center gap-2">
                              <Save size={16}/> 저장하기
                            </div>
                          </Button>
                        </div>
                      </div>
                    </TabPane>

                    {/* Tab 6: Amenity Master Management */}
                    <TabPane tabId="6">
                      <div className="space-y-6">
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h5 className="text-lg font-bold text-gray-900">편의시설 관리</h5>
                            <p className="text-sm text-gray-500 mt-1">
                              모든 지점에서 공통으로 사용할 편의시설 항목을 관리합니다.
                            </p>
                          </div>
                          <Button color="success" className="rounded-lg px-4" onClick={handleOpenCreateAmenity}>
                            <Plus size={16} className="mr-2"/>
                            편의시설 추가
                          </Button>
                        </div>

                        <AmenityList amenities={amenities} onEdit={handleOpenEditAmenity} onCopy={handleOpenCopyAmenity} onDelete={handleDeleteAmenity}/>

                        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <h6 className="text-sm font-semibold text-blue-900 mb-2">💡 안내사항</h6>
                          <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                            <li>편의시설은 모든 지점에서 공통으로 사용됩니다.</li>
                            <li>각 지점의 "홈 및 매장 설정" 탭에서 노출할 편의시설을 선택할 수 있습니다.</li>
                            <li>삭제된 편의시설은 모든 지점에서 표시되지 않습니다.</li>
                            <li>아이콘 이미지는 투명 배경의 PNG 파일을 권장합니다.</li>
                          </ul>
                        </div>
                      </div>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>

            {/* Right Column: Mobile Preview */}
            <Col lg={4}>
              <div className="sticky top-[100px] z-10 self-start">
                <Card className="border-0 shadow-sm rounded-xl overflow-hidden bg-gray-100 p-6 flex flex-col items-center justify-center min-h-[700px]">
                  <MobilePreview siteData={{
            name,
            slogan,
            address: addressRoad || address,
            detail_info: detailInfo
        }} heroImages={siteCarousel.length > 0 && siteCarousel[0].images ? siteCarousel[0].images.map((img: any) => img.src) : []} sectionVisibility={sectionVisibility} bottomBarVisibility={bottomBarVisibility} selectedAmenities={allAmenities.filter(a => selectedAmenityIds.includes(a.id))} mainTitleColor={mainTitleColor} showAddress={showAddress} isLoading={loading}/>
                </Card>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modals */}
      {isFacilityModalOpen && (<FacilityModal isOpen={isFacilityModalOpen} toggle={() => setIsFacilityModalOpen(!isFacilityModalOpen)} facility={selectedFacility} siteId={SITE_ID} isCopyMode={isCopyMode} onSave={(newFac) => {
                if (selectedFacility && !isCopyMode)
                    setFacilities(p => p.map(f => f.id === newFac.id ? newFac : f));
                else
                    setFacilities(p => [...p, newFac]);
            }}/>)}

      {isReviewModalOpen && (<ReviewModal isOpen={isReviewModalOpen} toggle={() => setIsReviewModalOpen(!isReviewModalOpen)} review={selectedReview} siteId={SITE_ID} onSave={(newRev) => {
                if (selectedReview)
                    setRecommendedReviews(p => p.map(r => r.id === newRev.id ? newRev : r));
                else
                    setRecommendedReviews(p => [...p, newRev]);
            }}/>)}

      {isDisplayMenuModalOpen && (<DisplayMenuModal isOpen={isDisplayMenuModalOpen} toggle={() => setIsDisplayMenuModalOpen(!isDisplayMenuModalOpen)} displayMenu={selectedDisplayMenu} siteId={SITE_ID} onSave={(newDisplayMenu) => {
                if (selectedDisplayMenu)
                    setDisplayMenus((prev) => prev.map((item) => item.id === newDisplayMenu.id ? newDisplayMenu : item));
                else
                    setDisplayMenus((prev) => [...prev, newDisplayMenu]);
            }}/>)}

      {carouselEditModal && (<CarouselModal key={`${carouselModalType}-${targetCarousel}-${carouselModalNumber}`} modalNumber={carouselModalNumber} carouselId={targetCarousel} setVisible={setCarouselEditModal} visible={carouselEditModal} site={SITE_ID} carouselData={carouselModalType === "gallery" ? galleryCarousels : siteCarousel} onSave={fetchData} carouselType={carouselModalType} titleLabel={carouselModalType === "gallery" ? "갤러리" : "히어로 캐러셀"}/>)}

      {kakaoMapEditModal && (<KakaoMapModal visible={kakaoMapEditModal} setVisible={setKakaoMapEditModal} setAddress={setAddressRoad}/>)}

      {isAmenityModalOpen && (<AmenityModal isOpen={isAmenityModalOpen} toggle={() => setIsAmenityModalOpen(!isAmenityModalOpen)} amenity={selectedAmenity} onSave={handleSaveAmenity} isCopyMode={isAmenityCopyMode}/>)}
    </React.Fragment>);
};
export default SiteSettings;
